import {
  getGoogleFontCssUrl,
  isFontPickerValueSelected,
  normalizeFontPickerValue,
} from '../components/Page/Components/Common/pageFontUtils';

export const DEFAULT_PAGE_PRIMARY_FONT_FAMILY = '"Source Sans Pro", sans-serif';

const getUsableUploadedPath = (path) => {
  if (!path || typeof path !== 'string') {
    return '';
  }

  const normalized = path.trim();
  if (!normalized) {
    return '';
  }

  if (
    normalized.startsWith('http://')
    || normalized.startsWith('https://')
    || normalized.startsWith('//')
  ) {
    return normalized;
  }

  if (normalized.startsWith('/')) {
    return normalized;
  }

  return `/${normalized.replace(/^\.*\/+/, '')}`;
};

const getPageFontRuntime = ({
  fontValue,
  fontFamilyName,
  fallbackUploadedUrl,
}) => {
  if (!isFontPickerValueSelected(fontValue) && !fallbackUploadedUrl) {
    return null;
  }

  const normalized = normalizeFontPickerValue(fontValue);

  if (normalized.source === 'google' && normalized.googleFamily) {
    const escapedGoogleFamily = normalized.googleFamily.replace(/'/g, String.fromCharCode(92, 39));
    return {
      css: `@import url('${getGoogleFontCssUrl(normalized.googleFamily, normalized.googleWeight || '400')}');`,
      family: `'${escapedGoogleFamily}', sans-serif`,
    };
  }

  const uploadedUrl = fallbackUploadedUrl
    || normalized.url
    || getUsableUploadedPath(normalized.path);
  if (!uploadedUrl) {
    return null;
  }

  return {
    css: `@font-face { font-family: '${fontFamilyName}'; src: url('${uploadedUrl}'); font-display: swap; }`,
    family: `'${fontFamilyName}', sans-serif`,
  };
};

export const resolvePageFontValues = (options = {}) => ({
  primaryValue: options.font_primary || options.fontPrimary || '',
  secondaryValue: options.font_secondary || options.fontSecondary || '',
});

export const buildPageFontPresentation = ({
  primaryValue,
  secondaryValue,
  primaryUploadedUrl,
  secondaryUploadedUrl,
}) => {
  const primaryFont = getPageFontRuntime({
    fontValue: primaryValue,
    fallbackUploadedUrl: primaryUploadedUrl,
    fontFamilyName: 'PagePrimaryFont',
  });

  const secondaryFont = getPageFontRuntime({
    fontValue: secondaryValue,
    fallbackUploadedUrl: secondaryUploadedUrl,
    fontFamilyName: 'PageSecondaryFont',
  });

  const fontStyles = [];
  if (primaryFont?.css) fontStyles.push(primaryFont.css);
  if (secondaryFont?.css) fontStyles.push(secondaryFont.css);

  return {
    fontStyles,
    pageFontVars: {
      '--page-primary-font-family': primaryFont?.family || DEFAULT_PAGE_PRIMARY_FONT_FAMILY,
      ...(secondaryFont?.family ? { '--page-secondary-font-family': secondaryFont.family } : {}),
    },
  };
};
